/** The DOM half of the bitmap font.
 *
 * Numerals drawn into the world - a resource total, an army-socket count, a
 * recruit price, a primary-skill figure - are bitmap glyphs here for the same
 * reason they are on the battlefield: anti-aliased vector digits over
 * hard-quantised pixel art read as a different medium laid on top of the game,
 * and once the sprites are crisp the text is the loudest thing left in the
 * frame. Measured on the shipped battle screenshot, one count plaque carried
 * 157 unique colours across its 319px interior; a two-colour numeral is 3.
 *
 * The mark is an SVG with `shape-rendering: crispEdges` at a whole-number
 * magnification, so every rectangle lands on a device pixel boundary and the
 * browser has nothing to interpolate. A canvas would work equally well and
 * would need a ref, an effect and a redraw on every theme change; the SVG
 * inherits `color` from the cascade, which is what lets each call site keep the
 * colour rule it already had.
 *
 * ## The table below is a RESHAPE of one font, not a second one
 *
 * The face is authored, with an editable ASCII picture per glyph, in
 * `packages/render/src/text/glyphs.ts`, and imported from there through the
 * deep `@game/render/text/glyphs` subpath. **Edit the authored table; there is
 * nothing to copy.** What is duplicated here is the LAYOUT - a second
 * implementation that emits SVG path data instead of Pixi rectangles - and
 * `packages/ui/test/bitmap-font.spec.ts` measures the same strings through both
 * to catch the two drifting apart.
 */

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import {
  FACES, GLYPHS, GLYPH_H, OUTLINE, descentOf, effectiveDevicePixelRatio, holdToFloor,
  faceOf as renderFaceOf, windowCapFloor,
} from "@game/render/text/glyphs";
import type { FaceId } from "@game/render/text/glyphs";
import { SERIF_FACES } from "./serifFace.js";
import type { SerifFace, SerifFaceId } from "./serifFace.js";

/**
 * The faces a React caption can be set in: the three authored pixel faces from
 * `@game/render`, plus the two serif faces generated in `serifFace.ts`, which
 * exist only on the DOM side - the arena stamps counts, not captions, and a
 * count is the HUD face's job.
 */
export type UiFaceId = FaceId | SerifFaceId;

/** The face's shape, whichever side authored it. */
type AnyFace = SerifFace | ReturnType<typeof renderFaceOf>;

const isSerif = (id: UiFaceId | undefined): id is SerifFaceId =>
  id === "serif" || id === "serifDisplay" || id === "serifCaption" || id === "serifTitle";

/** The face `id` names, serif or pixel; the HUD face when nothing is named. */
export function faceOf(id?: UiFaceId): AnyFace {
  return isSerif(id) ? SERIF_FACES[id] : renderFaceOf(id);
}

/**
 * The line box: the body of the face, plus a keyline only if one was asked for.
 *
 * A glyph may hang below it - the comma is two rows under the baseline, so is
 * the tail of a `y` - and those rows are ink, not metric. The SVG is drawn tall
 * enough to hold them and the ELEMENT reserves that band for every string in
 * the face, so every reading in a row occupies the same box whether or not it
 * happens to contain a comma. Without that, `20,000` and `20` would be
 * different heights and `align-items: center` would stop lining their digits
 * up. It used to be a negative bottom margin instead, which bought the same
 * alignment by letting the tail overflow - and an `overflow: hidden` ancestor
 * then cut it off. The tail is inside the box now, and the box grows by the
 * rows THIS string hangs and no more. See `bandHeightOf`.
 *
 * ## The keyline is OFF unless the call site asks
 *
 * It used to be dilated around every glyph in the build. Measured on the
 * layout: `227987` carried 72 pixels of body against 187 of keyline - 2.60x -
 * and not one background column survived anywhere in its box, because a
 * four-column `0` has a two-column counter that a 1px inward dilation closes,
 * and `TRACKING` is 1 with the halo eating a column from each side of the gap.
 * The classic at 800x600 measures 29% ink to 71% plate on `Month:`. See the header of
 * `render/src/text/layout.ts` for the whole argument; the short version is that
 * a keyline is for a string over art the build does not control, and almost
 * every string here sits on a plate the build painted.
 *
 * Naming a `keyline` COLOUR still turns it on: a call site that picked the
 * halo's colour was asking for a halo. Everything else opts in with `outline`.
 */
const lineHeightOf = (face: UiFaceId | undefined, keyline: number): number =>
  faceOf(face).capH + keyline * 2;

/**
 * The box the ELEMENT occupies when a call site asks for the face's whole
 * descent band rather than the one this string needs.
 *
 * The ink of a descender has to be INSIDE the element's box. It used to be
 * pulled back out with a negative bottom margin, which does not make ink
 * disappear - it makes it overflow, and the parent it overflows is sized by
 * that same negative margin and then clips it. Measured on the shipped
 * 1920x1080 adventure screenshot: the sidebar's `Experience` read `Exoerience`
 * and the town row's `Kingsmarch` read `Kinqsmarch`, the tail of the `p` and of
 * the `g` being the two rows hanging outside the box.
 *
 * Reserving the FACE's whole band for every string is one row further than
 * that, and it is a row the screens do not have. It buys one property - every
 * reading in a flex row is the same height whether or not it contains a comma -
 * and it charges `face.descent * scale` for it under `BASIC`, `EXPERT`, `42`
 * and every other string with nothing below its baseline. Shipped that way for
 * one round it cost eight of the hero sheet's eight skill plates: the plate is
 * a fixed grid row around a 96px icon, and four pixels under a caption that
 * does not descend is four pixels the icon takes out of the mastery rail below
 * it - `BASIC` disappeared behind the icon and `ADVANCED` read `ANCED`.
 *
 * So the band is per STRING by default and per FACE only where a caller says
 * `reserveDescent`, which is the one place the shared centre is worth a row:
 * a column of readings that must line up whatever digits and punctuation they
 * happen to contain.
 */
const bandHeightOf = (face: UiFaceId | undefined, keyline: number): number =>
  lineHeightOf(face, keyline) + faceOf(face).descent;

/** The default line box: the HUD face's body, with no keyline in it. */
export const LINE_H = GLYPH_H;

/**
 * Derived from the authored table in `@game/render`, not a copy of it.
 *
 * The glyphs used to be transcribed here by hand, guarded by a test that
 * compared the two tables. That works, but a guard that fails after the fact is
 * strictly worse than a shape that cannot diverge: this now imports the one
 * table and reshapes it, so editing a glyph in `glyphs.ts` moves both renderers.
 *
 * The import is the deep `@game/render/text/glyphs` subpath rather than the
 * package barrel on purpose - the barrel re-exports the Pixi label, and pulling
 * Pixi into the React bundle to read seven integers would be a poor trade.
 */
export const UI_GLYPHS: Readonly<Record<string, readonly number[]>> = Object.freeze(
  Object.fromEntries(
    Object.entries(GLYPHS).map(([ch, g]) => [ch, [g.w, ...g.rows] as readonly number[]]),
  ),
);

/**
 * The entry `ch` draws with in `face`. Anything unknown draws `?`.
 *
 * Lowercase used to fold up to its capital, because the face had no lowercase.
 * It has one now, and the mirror of `foldGlyphKey` must fold the same way or a
 * caption would be small caps in a React panel and mixed case on the arena.
 */
export function uiGlyphKey(ch: string, face?: UiFaceId): string {
  const table = faceOf(face).glyphs;
  if (Object.prototype.hasOwnProperty.call(table, ch)) return ch;
  const up = ch.toUpperCase();
  return Object.prototype.hasOwnProperty.call(table, up) ? up : "?";
}

/** How a string is set here: which face, and how much keyline it reserves. */
export interface BitmapTextStyle {
  readonly face?: UiFaceId | undefined;
  /** Glyph pixels of keyline on every side. 0 - the default - draws none. */
  readonly keyline?: number | undefined;
}

const keylineOf = (style?: BitmapTextStyle): number => {
  const k = style?.keyline ?? 0;
  return k > 0 ? Math.round(k) : 0;
};

/**
 * The luminance above which a named keyline colour is a HIGHLIGHT and not a
 * keyline, in the same Rec.709 the relief module measures in.
 *
 * A keyline is dark and its job is to hold a mark against ground the build does
 * not control. A call site that names a CREAM colour is asking for something
 * else - Hall of Fame's multiplier pills pass `#f7edcf` and `#fff6d8` against
 * dark ink on a tan plate - and what it gets from a four-sided halo is a mark
 * whose halo outweighs its body. Measured on `scores.1080p`, the `x 1.99` pill
 * carried 208 cream pixels to 156 of ink, 1.33x, and a reviewer read the three
 * numerals as one slab.
 *
 * Above this line the halo is treated as relief: it goes on the two sides the
 * light comes from and nowhere else, which is the same upper-left this kit's
 * `relief.ts` lights every plate from.
 */
const HIGHLIGHT_LUMA = 140;

/** Rec.709 luminance of `#rgb` / `#rrggbb`, or -1 for anything else. */
function keylineLuma(colour: string | undefined): number {
  if (colour === undefined || colour.charCodeAt(0) !== 35) return -1;
  const body = colour.slice(1);
  const full = body.length === 3
    ? `${body.charAt(0)}${body.charAt(0)}${body.charAt(1)}${body.charAt(1)}${body.charAt(2)}${body.charAt(2)}`
    : body;
  if (full.length !== 6) return -1;
  const v = Number.parseInt(full, 16);
  if (Number.isNaN(v)) return -1;
  return 0.2126 * ((v >> 16) & 255) + 0.7152 * ((v >> 8) & 255) + 0.0722 * (v & 255);
}

/** Box size of `text` in glyph pixels, keyline included. */
export function measureBitmapText(
  text: string, style?: BitmapTextStyle,
): { readonly width: number; readonly height: number; readonly descent: number } {
  const face = faceOf(style?.face);
  const key = keylineOf(style);
  let w = 0;
  let descent = 0;
  for (let i = 0; i < text.length; i++) {
    if (i > 0) w += face.tracking;
    const glyph = face.glyphs[uiGlyphKey(text.charAt(i), style?.face)]!;
    w += glyph.w;
    descent = Math.max(descent, descentOf(glyph));
  }
  return {
    width: w + key * 2,
    height: lineHeightOf(style?.face, key) + descent,
    descent,
  };
}

/**
 * Glyph pixels `text` hangs below its line box, in the face it is set in.
 *
 * Exported because a PANEL has to be able to owe itself the clearance. The
 * element now reserves the face's whole descent band, so the tail no longer
 * punches out of the box - but a rule drawn INSIDE the same box, under the
 * text, still has to know where the ink stops. Measured on
 * `town-castle.1080p`: the Income panel's cream rule at rows 37-38 had 119
 * bright columns and 10 dark ones, the dark ones being the comma of `4,000`
 * and the `y` of `day` notching through it. A rule that knows the descent can
 * drop by it.
 */
export function descentOfBitmapText(text: string, face?: UiFaceId): number {
  let d = 0;
  for (let i = 0; i < text.length; i++) {
    d = Math.max(d, descentOf(faceOf(face).glyphs[uiGlyphKey(text.charAt(i), face)]!));
  }
  return d;
}

/** The metrics of each face, for a call site that lays out against them. */
export const BITMAP_FACES = FACES;

interface Paths {
  readonly width: number;
  /** Drawn height, descender included. */
  readonly height: number;
  /** The box the element occupies. `height`, or the face's full band when the
   *  call site asked to reserve it. */
  readonly boxHeight: number;
  /** Glyph pixels of ink below the line box. */
  readonly descent: number;
  readonly ink: string;
  readonly keyline: string;
}

/**
 * The two SVG paths for `text`: the glyph bodies, and the keyline around them.
 *
 * Rows are run-length encoded, so a five-pixel run is one rectangle in the path
 * rather than five. The keyline surrounds the mark rather than sitting under
 * one side of it, because a plaque numeral has to stay legible over a bright
 * sprite *and* over near-black wood, and a drop shadow only ever solves one of
 * the two. It grows into the EXTERIOR only, by the four sides of a pixel rather
 * than its corners, and never into the column `TRACKING` leaves between two
 * glyphs - see the flood below.
 */
function pathsFor(
  text: string, style?: BitmapTextStyle, band = false, lit = false,
): Paths {
  const { width, height, descent } = measureBitmapText(text, style);
  const boxHeight = band
    ? Math.max(height, bandHeightOf(style?.face, keylineOf(style)))
    : height;
  const face = faceOf(style?.face);
  const key = keylineOf(style);
  const body = new Uint8Array(width * height);
  // The columns TRACKING leaves between two glyphs, marked as the run's own
  // gap rather than as ground. Nothing paints into them - see `exteriorHalo`.
  const gap = new Uint8Array(width);
  let pen = key;
  for (let i = 0; i < text.length; i++) {
    const glyph = face.glyphs[uiGlyphKey(text.charAt(i), style?.face)]!;
    const gw = glyph.w;
    if (i > 0) for (let x = pen - face.tracking; x < pen; x++) gap[x] = 1;
    for (let row = 0; row < face.capH + descentOf(glyph); row++) {
      const bits = glyph.rows[row] ?? 0;
      for (let col = 0; col < gw; col++) {
        if ((bits & (1 << (gw - 1 - col))) === 0) continue;
        body[(row + key) * width + pen + col] = 1;
      }
    }
    pen += gw + face.tracking;
  }

  const halo = key > 0
    ? exteriorHalo(body, gap, width, height, key, lit)
    : new Uint8Array(0);
  return {
    width, height, boxHeight, descent,
    ink: pathOf(body, width, height),
    keyline: key > 0 ? pathOf(halo, width, height) : "",
  };
}

/**
 * The keyline: background within `key` of the ink, REACHABLE FROM OUTSIDE, and
 * not standing in the run's own tracking column.
 *
 * Three rules, each of which removes a way this mark stopped being a numeral.
 *
 * **Exterior only.** A plain dilation grows into a counter as readily as into
 * the ground, and at this size the counter loses. A digit is four columns wide
 * with a two-column counter, so one pixel of inward growth from each wall
 * closes it completely - measured on the shipped hero sheet, the army sockets'
 * keylined `58` and `31` at magnification 4 were a black slab with two digit
 * silhouettes cut out of it. A keyline exists to separate ink from the GROUND
 * behind it; a counter has no ground behind it to separate from. The flood is
 * 4-connected on purpose: an 8-connected flood walks through the diagonal
 * corner where `.##.` meets `#..#` and every counter in the face is "outside".
 *
 * **Manhattan, not Chebyshev** - the four sides of a pixel, not its corners. A
 * corner cell is covered anyway wherever two edges meet at it, so what the
 * diagonals buy is the fill in every notch the face draws: the throat of the
 * `5`, the waist of the `3`, the crotch of the `4`.
 *
 * **Never in a tracking column.** `TRACKING` is one glyph pixel, so the single
 * column between two digits is within reach of both of them and a halo that
 * grows horizontally claims all of it. That is what made `58` one black tile
 * with two cream shapes cut out of it even after the counters opened: the
 * counters were legible and the RUN was not. Between two glyphs of one run
 * there is no ground to separate from - the thing that separates them IS the
 * tracking column - so the halo stops at its edge and the column keeps the
 * plate's own colour. Both flanks of the run still carry theirs, which a
 * left-only or vertical-only halo gives up.
 *
 * **A bright keyline is a highlight and takes two sides, not four.** See
 * `HIGHLIGHT_LUMA`: a call site that names a cream colour is asking for relief,
 * and relief has a direction. `lit` restricts the reach to the ink's lower
 * right, which paints the halo above and to the left of every stroke.
 *
 * Counted on the keylined `58` at OUTLINE 1: full 8-neighbour dilation is 67
 * keyline pixels against 28 of body (2.39x), exterior-only 8-neighbour 59
 * (2.11x), exterior Manhattan 43 (1.54x), and this is 37 (1.32x), with the
 * gap column empty in all seven rows instead of six of them. The classic measures
 * 29% ink to 71% plate on `Month:`; a mark that is two and a half times
 * keyline has stopped being a numeral on a ground and become a black slab with
 * scratches in it.
 */
function exteriorHalo(
  body: Uint8Array, gap: Uint8Array, width: number, height: number, key: number,
  lit: boolean,
): Uint8Array {
  const outside = new Uint8Array(width * height);
  const stack: number[] = [];
  const reach = (i: number): void => {
    if (body[i] === 1 || outside[i] === 1) return;
    outside[i] = 1;
    stack.push(i);
  };
  for (let x = 0; x < width; x++) { reach(x); reach((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { reach(y * width); reach(y * width + width - 1); }
  while (stack.length > 0) {
    const i = stack.pop()!;
    const x = i % width;
    const y = (i - x) / width;
    if (x > 0) reach(i - 1);
    if (x + 1 < width) reach(i + 1);
    if (y > 0) reach(i - width);
    if (y + 1 < height) reach(i + width);
  }
  const halo = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (outside[i] !== 1 || gap[x] === 1) continue;
      for (let dy = -key; dy <= key && halo[i] !== 1; dy++) {
        for (let dx = -key; dx <= key; dx++) {
          if (Math.abs(dx) + Math.abs(dy) > key) continue;
          // A highlight sits between the light and the ink, so it may only be
          // reached from the ink's lower right - which puts it above and to the
          // left of every stroke, and leaves the run's own right flank clear.
          if (lit && (dx < 0 || dy < 0)) continue;
          const ny = y + dy, nx = x + dx;
          if (ny < 0 || ny >= height || nx < 0 || nx >= width) continue;
          if (body[ny * width + nx] === 1) { halo[i] = 1; break; }
        }
      }
    }
  }
  return halo;
}

/** A 0/1 grid as one path of axis-aligned rectangles, run-length encoded. */
function pathOf(grid: Uint8Array, width: number, height: number): string {
  let d = "";
  for (let y = 0; y < height; y++) {
    let x = 0;
    while (x < width) {
      if (grid[y * width + x] !== 1) { x++; continue; }
      const start = x;
      while (x < width && grid[y * width + x] === 1) x++;
      d += `M${String(start)} ${String(y)}h${String(x - start)}v1h-${String(x - start)}z`;
    }
  }
  return d;
}

/**
 * A whole-number magnification that steps with the window.
 *
 * The kit's own `--hm-scale` is 1.5 at 1080p, and a fractional magnification is
 * exactly the resampling this file exists to remove - so a bitmap reading picks
 * one of two WHOLE sizes instead. The step is by window width rather than by
 * the element's own box because that is the axis the screens' own compact
 * stylesheets already switch on, and a reading that stepped independently of
 * the panel around it would be worse than one that never stepped at all.
 *
 * Returns `base` on a wide window and `base - 1` (never below 1) on a narrow
 * one. Safe with no `window`: it assumes the wide case, which is what the
 * markup snapshot in a test should show.
 */
export function useBitmapScale(base: number, wideAtLeast = 1500): number {
  // Measured in DEVICE pixels, not by a media query on the CSS viewport: at
  // 80% zoom a 1366-wide window is a 1707-wide viewport, which is "wide" by
  // the query and is not - it is the same 1366 device columns the layout had
  // at 100%, and the readings that stepped down to fit them at 100% have no
  // more room at 80%. Above a ratio of 1 the CSS width is the one the layout
  // is laid out in and is used as is.
  const read = (): boolean => {
    if (typeof window === "undefined") return true;
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    return window.innerWidth * Math.min(1, dpr) >= wideAtLeast;
  };
  const [wide, setWide] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChange = (): void => { setWide(read()); };
    onChange();
    // A zoom change arrives as a resize; so does the window being resized.
    window.addEventListener("resize", onChange);
    return () => { window.removeEventListener("resize", onChange); };
  }, [wideAtLeast]);
  return wide ? base : Math.max(1, base - 1);
}

/**
 * The browser's device pixel ratio, kept current across zoom changes.
 *
 * Chrome fires no event for Ctrl+plus; what it does is stop matching a
 * `(resolution: Xdppx)` query for the old ratio, so the hook subscribes to the
 * exact ratio it last read and re-reads when that stops being true. 1 with no
 * window, which is what the markup in a test should assume.
 */
export function useDevicePixelRatio(): number {
  const read = (): number =>
    typeof window === "undefined" || !(window.devicePixelRatio > 0) ? 1 : window.devicePixelRatio;
  const [dpr, setDpr] = useState<number>(read);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(resolution: ${String(dpr)}dppx)`);
    const onChange = (): void => { setDpr(read()); };
    mq.addEventListener("change", onChange);
    // A resize is the other way a ratio moves (a window dragged between two
    // monitors), and the cheap thing is to re-read on both.
    window.addEventListener("resize", onChange);
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, [dpr]);
  return dpr;
}

/**
 * The two face families, each in ascending cap order, so a reading held to the
 * floor can be promoted to the next face of its own kind and never across the
 * sans/serif line - a caption that changed letterform between two zoom levels
 * would be a different caption.
 */
const FAMILIES: readonly (readonly UiFaceId[])[] = [
  // `tall` before `display` at the same cap: a floor promotion takes the
  // narrower face, which is the one a fixed sidebar groove has room for.
  // `caption` (15) and `serifCaption` (15) are where a 1080p floor lands.
  // `serifTitle` (19) is where a scale-2 serif title lands under an 80%
  // page: nearest to the 22.4 rows it asked for at magnification 1, so the
  // hero's name keeps a step over its level line instead of flooring to it.
  ["hud", "text", "tall", "display", "caption"],
  ["serif", "serifDisplay", "serifCaption", "serifTitle"],
  // The condensed roman family, at the three caps the floor lands on: 11 on a
  // 768-tall window, 14 where a HUD call site asked for two pixels per texel
  // there, 15 on a 1080-tall one. A HUD reading set in `roman` therefore
  // stands the same device rows it stood in `tall` / `caption`, in a serif.
  ["roman", "romanDisplay", "romanCaption"],
];

/**
 * The cap floor for the window this render is in, kept current across a
 * zoom or a resize. It is the window's DEVICE height that sets it (see
 * `deviceCapFloor`), so a zoom-out, which leaves the device height alone,
 * leaves the floor alone too: the same face at the same rows, in fewer CSS
 * pixels each. `MIN_DEVICE_CAP` with no window.
 */
export function useDeviceCapFloor(): number {
  const [floor, setFloor] = useState<number>(windowCapFloor);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChange = (): void => { setFloor(windowCapFloor()); };
    onChange();
    window.addEventListener("resize", onChange);
    return () => { window.removeEventListener("resize", onChange); };
  }, []);
  return floor;
}

/** Which face a reading is actually stamped in, and at what magnification. */
export interface ResolvedBitmapSize {
  readonly face: UiFaceId;
  /** Whole device pixels per glyph texel. */
  readonly magnification: number;
  /** CSS pixels per glyph texel - `magnification / ratio`. */
  readonly cssPerTexel: number;
  /** The cap height that reaches the glass, in device pixels. */
  readonly deviceCap: number;
}

/**
 * Where a reading of `face` at `scale` lands under `devicePixelRatio`.
 *
 * Two rules from `@game/render`'s `devicePixels.ts`, applied in order: the
 * magnification is snapped to a whole number of DEVICE pixels per texel (a
 * 125%-zoomed page draws a scale-2 reading at 3 device px per texel, 2.4 CSS
 * px; an 80% page draws it at 1, 1.25 CSS px), and then the reading is held
 * to the window's cap floor by promoting it to the next face of its family
 * or the next magnification, whichever clears the floor by the least - a
 * HUD or text reading at 1 becomes the tall face at 1 on a laptop window
 * and the caption face at 1 on a 1080p one. A call site that lays out
 * against a measurement uses this too, so what it measures is what gets
 * drawn.
 *
 * `floor` is `true` for the window's own floor (`windowCapFloor`), `false`
 * for none, or a number of device pixels.
 */
export function resolveBitmapSize(
  face: UiFaceId | undefined, scale: number, devicePixelRatio: number, floor: boolean | number = true,
): ResolvedBitmapSize {
  const ratio = effectiveDevicePixelRatio(devicePixelRatio);
  const id = face ?? "hud";
  const family = FAMILIES.find((f) => f.includes(id)) ?? [id];
  const caps = family.map((f) => faceOf(f).capH);
  const floorPx = floor === true ? windowCapFloor() : floor === false ? 0 : floor;
  const held = holdToFloor(caps, family.indexOf(id), scale, ratio, floorPx);
  const chosen = family[held.face] ?? id;
  return {
    face: chosen,
    magnification: held.magnification,
    cssPerTexel: held.magnification / ratio,
    deviceCap: faceOf(chosen).capH * held.magnification,
  };
}

/**
 * Drawn box of `text` at `scale`, in CSS pixels, AFTER the floor and the
 * device snap - the box `BitmapText` will actually occupy. A call site that
 * measures the glyph table directly and lays out against it is measuring the
 * face it asked for, not the one that gets stamped: at magnification 1 the
 * HUD face is promoted to the display face, which sets `Movement` half again
 * as wide, and a groove sized off the HUD width had its reading overprint its
 * caption at 1366x768.
 */
export function measureDrawnBitmapText(
  text: string, scale: number, face?: UiFaceId, devicePixelRatio = 1, floor: boolean | number = true,
): { readonly width: number; readonly height: number } {
  const size = resolveBitmapSize(face, Math.max(1, Math.round(scale)), devicePixelRatio, floor);
  const m = measureBitmapText(text, { face: size.face });
  return { width: m.width * size.cssPerTexel, height: m.height * size.cssPerTexel };
}

/**
 * The largest whole magnification, `maxScale` down to 1, at which the widest
 * of `texts` fits `room` CSS pixels once it is drawn - floor applied - or 1
 * when none does. The adventure sidebar's gauges, skill rail and status plate
 * pick one step for a whole stack this way, so a band of three plates never
 * carries two sizes of one face.
 */
export function fitBitmapMagnification(
  room: number, texts: readonly string[], maxScale = 2, face?: UiFaceId, devicePixelRatio = 1,
  floor: boolean | number = true,
): number {
  const top = Math.max(1, Math.trunc(maxScale));
  if (room <= 0 || texts.length === 0) return 1;
  for (let s = top; s > 1; s--) {
    let widest = 0;
    // Under the ratio the reading will actually be drawn at: a zoomed-out
    // page floors the magnification and promotes the face, and a step that
    // fit at 100% may draw wider than the groove at 80%.
    for (const t of texts) widest = Math.max(widest, measureDrawnBitmapText(t, s, face, devicePixelRatio, floor).width);
    if (widest <= room) return s;
  }
  return 1;
}

/**
 * The first of `candidates` that fits `room` CSS pixels once drawn - floor
 * applied, under the ratio the page is at - or the last of them when none
 * does. A caption with a clipped ancestor names its wordings longest first
 * (`Level 1 - Knight`, `Level 1 Knight`, `Level 1`) and gets the fullest one
 * its groove holds, instead of the fullest one cut to `Level 1 - Knigh`.
 *
 * A bitmap reading cannot be squeezed: the floor holds its magnification and
 * face, and the groove it sits in is narrower on the glass under an 80%
 * page than at 100% while the reading is not. Measured on the adventure
 * sidebar at 80% on a 1920x1080 window before this existed, the name
 * column left 122 device pixels for a 125-pixel caption; on a 1366x768
 * window at 80% it was 95 for 98. Dropping a wording, not a glyph.
 *
 * `shadow` adds the one-texel drop shadow `BitmapText` draws to the box, so
 * a caption that asks for one is measured as it is drawn. `floor` is the
 * window's own cap floor by default (`windowCapFloor`), which is
 * `MIN_DEVICE_CAP` wherever there is no window; a test that wants the
 * 1080p floor names it in device pixels, as `resolveBitmapSize` takes it.
 */
export function fitBitmapText(
  room: number, candidates: readonly string[], scale = 1, face?: UiFaceId, devicePixelRatio = 1, shadow = false,
  floor: boolean | number = true,
): string {
  const last = candidates[candidates.length - 1] ?? "";
  if (!(room > 0)) return last;
  const extra = shadow ? resolveBitmapSize(face, Math.max(1, Math.round(scale)), devicePixelRatio, floor).cssPerTexel : 0;
  for (const text of candidates) {
    if (measureDrawnBitmapText(text, scale, face, devicePixelRatio, floor).width + extra <= room) return text;
  }
  return last;
}

/**
 * The roman face a call site that was tuned in HUD steps should ask for.
 *
 * The sans family carried its sizes as MAGNIFICATIONS of a seven-row face: a
 * plaque figure asked for `scale={2}` and got the HUD face drawn at two CSS
 * pixels per texel, 14 device rows on a 768-tall window. The roman family
 * carries the same sizes as FACES - 11, 14 and 15 - because a small face
 * drawn twice as big is a small face drawn twice as big: its stems double
 * with it and its counters do not open, which is why the sans figures read as
 * blocky beside the serif captions on the same plate. So a call site that
 * used to pass a step passes the face for that step and draws it at one
 * device pixel per texel: `step` 2 is `romanDisplay` (14) and anything less
 * is `roman` (11), and the 1080p floor promotes either to `romanCaption` at
 * 15, which is exactly where the doubled HUD face landed there.
 */
export function romanFaceForStep(step: number): UiFaceId {
  return step >= 2 ? "romanDisplay" : "roman";
}

/**
 * The step - 2 or 1 - whose roman face fits `room` CSS pixels for every one of
 * `texts`, measured as it will be drawn (floor applied, under the ratio the
 * page is at). The roman answer to `fitBitmapMagnification`, which measures
 * MAGNIFICATIONS of one face; a band of readings picks one step for the whole
 * band this way, so no two plates in a row carry two sizes.
 */
export function fitRomanStep(
  room: number, texts: readonly string[], devicePixelRatio = 1, floor: boolean | number = true,
): number {
  if (!(room > 0) || texts.length === 0) return 1;
  let widest = 0;
  for (const t of texts) {
    widest = Math.max(widest, measureDrawnBitmapText(t, 1, "romanDisplay", devicePixelRatio, floor).width);
  }
  return widest <= room ? 2 : 1;
}

export interface BitmapTextProps {
  /** What to set. A number is stringified; format it before passing it. */
  readonly children: string | number;
  /**
   * Whole-number magnification of the glyph pixels. It is deliberately NOT tied
   * to `--hm-scale`, which is 1.5 at 1080p: a fractional magnification is the
   * resampling this whole file exists to remove.
   */
  readonly scale?: number | undefined;
  /**
   * Which of the faces to set in.
   *
   *   `"hud"`     cap 7,  x 5, 1px stem - the default; counts and tight readings
   *   `"text"`    cap 9,  x 6, 1px stem - panel captions and running labels
   *   `"tall"`    cap 11, x 8, 2px stem - the text face's widths at the floor
   *   `"display"` cap 11, x 7, 2px stem - screen headings and the hero figure
   *   `"caption"` cap 15, x 11, 2px stem - the 1080p floor's own face
   *
   * the condensed roman family authored beside them - `"roman"` (cap 11),
   * `"romanDisplay"` (14) and `"romanCaption"` (15) - which is what the HUD,
   * the sidebar and the resource bars are set in, and the serif family from
   * `serifFace.ts`: `"serif"` (cap 11),
   * `"serifDisplay"` (14), `"serifCaption"` (15, the 1080p floor) and
   * `"serifTitle"` (19, where a scale-2 serif title lands under an 80% page).
   *
   * The classic ships three faces for this reason, and the seven-row one drawn bigger
   * is not the same thing as a heavier one: a display face has stems twice the
   * weight of its counters, which is what lets a title hold without an outline.
   */
  readonly face?: UiFaceId | undefined;
  /**
   * A one-pixel dark shadow down and to the right of every stroke - the
   * treatment the classic gives every serif caption on a dark plate. It is a
   * second copy of the ink path, not a filter, so it stays on whole pixels at
   * every magnification and costs no blur. Off by default; the serif faces are
   * what it is for.
   */
  readonly shadow?: boolean | undefined;
  /**
   * Draw the dark keyline around every glyph. **Off by default.** Turn it on
   * only where the build does not control what is behind the string - a count
   * printed straight onto a creature, a reading over the map. On a plate the
   * build painted it costs 2.4-2.6x the body's own ink and closes every counter
   * and every letter gap in the string; see the header of this file.
   *
   * Passing a `keyline` COLOUR implies `outline`, because a call site that
   * chose the halo's colour was asking for a halo.
   */
  readonly outline?: boolean | undefined;
  /**
   * Reserve the FACE's whole descent band, not just this string's.
   *
   * Off by default, and the default is the one that keeps a screen's layout:
   * the box holds exactly the rows this string hangs, so `BASIC`, `EXPERT` and
   * `42` cost nothing at all and only a caption that actually descends is a row
   * or two taller. Turn it on for a COLUMN of readings that must share a centre
   * whatever they happen to contain - the resource bar, where `20,000` sits
   * beside `20` and a comma may not make one of them taller. See
   * `bandHeightOf` for what reserving it everywhere cost.
   */
  readonly reserveDescent?: boolean | undefined;
  /** Keyline colour. The body always takes `currentColor` from the cascade. */
  readonly keyline?: string | undefined;
  /**
   * Hold the reading to `MIN_DEVICE_CAP` device pixels of cap. **On by
   * default**: a reading that would reach the glass smaller than the original
   * game's smallest face is promoted to the next face of its family or the
   * next magnification (see `resolveBitmapSize`). Off only for a mark whose
   * box cannot move and whose size is the design - nothing in the six shipped
   * scenes needs it, and a call site that reaches for it is giving up the one
   * property this file exists to guarantee. A NUMBER is a floor of that many
   * device pixels of cap in place of the window's: the terrain plate on a
   * desktop holds to the original game's smallest face rather than to the
   * 1080p caption, at the player's asking, and states the number it holds to.
   */
  readonly floor?: boolean | number | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly title?: string | undefined;
}

/**
 * A run of bitmap text: a count, a total, a price, a stat figure, and now the
 * caption over any of them.
 *
 * The face carries lowercase with real descenders, so a label no longer has to
 * leave it to be set in mixed case - The classic stamps every string on its hero
 * screen, `Experience` and `Spell Points` included, and a caption set in an
 * anti-aliased serif beside a bitmap figure is the "two programs in one frame"
 * tell. Running body prose - a skill's paragraph of rules text - is still the
 * serif's job, though less of it than before: there are three faces now, so a
 * heading and a caption no longer have to be the same drawing at two sizes.
 */
export function BitmapText(props: BitmapTextProps): JSX.Element {
  const text = String(props.children);
  const dpr = useDevicePixelRatio();
  // Read for its subscription: the floor itself is read again inside
  // `resolveBitmapSize`, and this is what re-renders the reading when the
  // window's device height changes.
  useDeviceCapFloor();
  // The magnification the call site asked for is CSS pixels per texel; what
  // is drawn is a whole number of DEVICE pixels per texel, held to the floor.
  // At 100% zoom on a 1x display the two agree exactly and nothing here moves.
  // Browser zoom is honoured either way: at 80% the layout around this
  // reading is four fifths the size and the reading is whatever whole device
  // magnification clears the floor - see `resolveBitmapSize`.
  const size = resolveBitmapSize(props.face, Math.max(1, Math.round(props.scale ?? 2)), dpr, props.floor ?? true);
  const scale = size.cssPerTexel;
  const face = size.face;
  // Naming the halo's colour is asking for a halo; `outline` is the explicit
  // way to say it. Everything that says neither gets the clean face.
  const keyline = props.outline === true || props.keyline !== undefined ? OUTLINE : 0;
  const band = props.reserveDescent === true;
  const shadow = props.shadow === true ? 1 : 0;
  const lit = keylineLuma(props.keyline) > HIGHLIGHT_LUMA;
  const paths = useMemo(
    () => pathsFor(text, { face, keyline }, band, lit), [text, face, keyline, band, lit],
  );
  return (
    <svg
      className={props.className}
      style={{
        display: "inline-block",
        verticalAlign: "middle",
        flex: "0 0 auto",
        // The svg's OWN viewport must not clip. Under a fractional ratio the
        // element's box lands on a fractional device pixel while crispEdges
        // snaps every rectangle to a whole one, so an edge column of texels
        // can be snapped one device pixel past the box - and an svg clips to
        // its box by default, leaving that column at 20-70% coverage.
        // Measured on the adventure sidebar at 1366x768 under an 80% page:
        // the Mana reading `10 / 10` came out `10 / 1C`, the last `0`'s right
        // stem at luma 108-132 against 248 for its left one. Reproduced in
        // isolation (round3/synth_clip.mjs): a 42-texel run at 1.25 CSS px per
        // texel on a 0.8 page lost its first column to 178/255 with the
        // default clip and kept 255/255 with `overflow: visible`, at every
        // fractional offset tried.
        overflow: "visible",
        ...props.style,
      }}
      // Fractional CSS pixels when the device ratio is fractional, on purpose:
      // 37 texels at 2 device px each on a 1.25 page is 59.2 CSS px, and that
      // is the only width that puts every texel edge on a device pixel.
      width={(paths.width + shadow) * scale}
      // The tail of a `p` is INSIDE the box, so a clipped ancestor cannot turn
      // `Experience` into `Exoerience`. It is the rows THIS string hangs unless
      // `reserveDescent` asks for the face's whole band - see `bandHeightOf`.
      height={(paths.boxHeight + shadow) * scale}
      data-cap={size.deviceCap}
      viewBox={`0 0 ${String(paths.width + shadow)} ${String(paths.boxHeight + shadow)}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={props.title ?? text}
    >
      {props.title === undefined ? null : <title>{props.title}</title>}
      {paths.keyline === "" ? null
        : <path d={paths.keyline} fill={props.keyline ?? "#0a0704"} />}
      {shadow === 0 || paths.ink === "" ? null
        : <path d={paths.ink} transform="translate(1 1)" fill="#0a0704" fillOpacity={0.82} />}
      {paths.ink === "" ? null : <path d={paths.ink} fill="currentColor" />}
      {/* The string once more as real text, drawn with NO ink. It is here for
          Chrome's compositor, not for a reader. A panel that overlaps a
          composited canvas (the adventure roster under the minimap, the whole
          town bar over the town scene) is promoted to its own layer, and cc
          rasterises a layer that holds no text at a snapped scale - 1 CSS px
          per raster px - and lets the GPU scale it to the glass. Under an 80%
          page that is a 1.25x downsample of every texel, and it is where the
          soft edges came from: measured on the 1920x1080 adventure shot at
          80% before this existed, `Heroes` had 23% of its ink pixels blended
          (a 3-texel stem drawn 0.5 / 1 / 1 / 0.45) while `Level 1 Knight`, in
          a panel Chrome had not promoted, was exact to the pixel. A layer
          that holds a text run is rasterised at the device scale instead, and
          a transparent run counts (`font-size: 0` does not - nothing is
          recorded). Verified in isolation on a translateZ layer at 0.8:
          4 of 16 texels exact without it, 16 of 16 with it. */}
      <text
        x={0}
        y={paths.boxHeight}
        fontSize={1}
        fill="transparent"
        aria-hidden="true"
        style={{ pointerEvents: "none", userSelect: "none" }}
      >
        {text}
      </text>
    </svg>
  );
}
